import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { SlidersHorizontal, Undo2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { EmptyTable, Pager, SortableHeader, TableToolbar } from '../components/data-table'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarSlot } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { type AuditEntry, api } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { notify } from '../lib/notify'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity-log',
  component: ActivityLogPage,
})

const RESOURCE_LABELS: Record<string, string> = {
  time_entry: 'Zeiteintrag',
  work_time: 'Stempeluhr',
  absence: 'Abwesenheit',
  document: 'Dokument',
  document_signature: 'Unterschrift',
  invoice: 'Rechnung',
  invoice_item: 'Rechnungsposition',
  customer: 'Kunde',
  project: 'Projekt',
  service: 'Tätigkeit',
  tag: 'Tag',
  user: 'Benutzer',
  invoice_settings: 'Rechnungs-Layout',
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Angelegt',
  update: 'Geändert',
  delete: 'Gelöscht',
  archive: 'Archiviert',
  unarchive: 'Reaktiviert',
  start: 'Timer gestartet',
  stop: 'Timer gestoppt',
  clock_in: 'Einstempeln',
  clock_out: 'Ausstempeln',
  break_in: 'Pause gestartet',
  break_out: 'Pause beendet',
  send: 'Versendet',
  sign: 'Unterzeichnet',
  reject: 'Abgelehnt',
  upload: 'Hochgeladen',
  login: 'Anmeldung',
  logout: 'Abmeldung',
  status_change: 'Status geändert',
  bulk_delete: 'Mehrfach-Löschung',
}

const ACTION_TONES: Record<string, string> = {
  create: 'bg-emerald-500/15 text-emerald-500',
  update: 'bg-bg-overlay text-text-secondary',
  delete: 'bg-red-500/15 text-red-500',
  bulk_delete: 'bg-red-500/15 text-red-500',
  archive: 'bg-bg-overlay text-text-tertiary',
  unarchive: 'bg-bg-overlay text-text-secondary',
  start: 'bg-accent/15 text-accent',
  stop: 'bg-bg-overlay text-text-secondary',
  clock_in: 'bg-emerald-500/15 text-emerald-500',
  clock_out: 'bg-bg-overlay text-text-secondary',
  break_in: 'bg-amber-500/15 text-amber-500',
  break_out: 'bg-bg-overlay text-text-secondary',
  send: 'bg-accent/15 text-accent',
  sign: 'bg-emerald-500/15 text-emerald-500',
  reject: 'bg-red-500/15 text-red-500',
  upload: 'bg-accent/15 text-accent',
  login: 'bg-bg-overlay text-text-secondary',
  logout: 'bg-bg-overlay text-text-tertiary',
  status_change: 'bg-bg-overlay text-text-secondary',
}

/**
 * Mirror of AuditLogService.isRevertable() — keep in sync with the backend.
 */
function isRevertable(entry: AuditEntry): boolean {
  if (entry.revertedAt) return false
  const supportedResources = ['customer', 'project', 'service', 'tag', 'time_entry', 'absence']
  if (!supportedResources.includes(entry.resource)) return false
  return ['delete', 'bulk_delete', 'archive', 'unarchive'].includes(entry.action)
}

function todayMinusDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

function fmtTime(s: string): string {
  const d = new Date(s)
  return `${d.toLocaleDateString('de-DE')} ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
}

function ActivityLogPage() {
  const queryClient = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: !!meData?.user && meData.user.role === 'admin',
  })

  const revertMutation = useMutation({
    mutationFn: (id: string) => api.revertAuditEntry(id),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['audit-log'] })
      // Also invalidate the lists that may now have restored data
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['services'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['absences'] })
      // Time-entry lists are cached under ['entries', ...] (calendar,
      // entry-list, calendar-timer); ['time-entries'] only holds the
      // last-selection query. Invalidate both so a reverted entry shows.
      queryClient.invalidateQueries({ queryKey: ['entries'] })
      queryClient.invalidateQueries({ queryKey: ['time-entries'] })
      // Wiederhergestellte Einträge/Abwesenheiten ändern Ist/Saldo/Urlaub —
      // ohne diese blieben Widgets + Berichte im selben Fenster stale.
      queryClient.invalidateQueries({ queryKey: ['absence-status'] })
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(`${r.restored} Datensatz/Datensätze wiederhergestellt`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Server-side filters
  const [userFilter, setUserFilter] = useState('')
  const [resourceFilter, setResourceFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [from, setFrom] = useState<string>(todayMinusDays(30))
  const [to, setTo] = useState<string>('')
  // Mobile-Shell (<640): Filter-Sheet hinterm Bottom-Slot (bündelt die
  // Filter-Karte: Mitarbeiter/Bereich/Aktion/Zeitraum), Titel oben —
  // read-only-Seite, kein ＋ (Mapping §3).
  const [filterSheet, setFilterSheet] = useState(false)
  useMobilePageTitle('Verlauf')

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', { userFilter, resourceFilter, actionFilter, from, to }],
    queryFn: () =>
      api.listAuditLog({
        userId: userFilter || undefined,
        resource: resourceFilter || undefined,
        action: actionFilter || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 1000,
      }),
    enabled: !!meData?.user,
  })

  const entries = useMemo(() => data?.entries ?? [], [data])

  const table = useDataTable<AuditEntry>(
    entries,
    {
      time: (e) => new Date(e.createdAt),
      user: (e) => e.userName ?? '',
      action: 'action',
      resource: 'resource',
    },
    {
      searchableFields: ['userName', 'userEmail', 'action', 'resource', 'resourceId', 'ip'],
      defaultSortKey: 'time',
      defaultSortDir: 'desc',
      defaultPageSize: 50,
      storageKey: 'audit-log',
    },
  )

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const isAdmin = meData.user.role === 'admin'
  const hasActiveFilter =
    userFilter !== '' || resourceFilter !== '' || actionFilter !== '' || to !== ''

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): Filter → Sheet — kein ＋ (read-only). */}
      <MobileBottomSlots>
        <BarSlot
          label="Filter"
          icon={<SlidersHorizontal className="h-5 w-5" />}
          active={hasActiveFilter}
          ariaLabel="Verlauf filtern"
          ariaHasPopup
          onClick={() => setFilterSheet(true)}
        />
      </MobileBottomSlots>

      {/* Filter-Sheet (<640): dieselben States wie die Filter-Karte —
          Änderungen filtern live hinter dem Sheet, schließen per Backdrop/X. */}
      <SidePanel open={filterSheet} onClose={() => setFilterSheet(false)} title="Filter">
        <div className="space-y-4">
          {isAdmin && (
            <div className="space-y-1">
              <Label>Mitarbeiter</Label>
              <Select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
                <option value="">Alle</option>
                {(usersData?.users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label>Bereich</Label>
            <Select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)}>
              <option value="">Alle</option>
              {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Aktion</Label>
            <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              <option value="">Alle</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Von</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Bis</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell-Top-Leiste — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader title="Verlauf" />

      {/* Server-side filters — <640 übernimmt das Filter-Sheet. */}
      <section className="phone:hidden rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {isAdmin && (
            <div className="space-y-1">
              <Label>Mitarbeiter</Label>
              <Select value={userFilter} onChange={(e) => setUserFilter(e.target.value)}>
                <option value="">Alle</option>
                {(usersData?.users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label>Bereich</Label>
            <Select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)}>
              <option value="">Alle</option>
              {Object.entries(RESOURCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Aktion</Label>
            <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              <option value="">Alle</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Von</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Bis</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </section>

      <TableToolbar
        search={table.search}
        onSearch={table.setSearch}
        filteredRows={table.filteredRows}
        totalRows={table.totalRows}
        pageSize={table.pageSize}
        onPageSize={table.setPageSize}
      />

      <TableShell className="rounded-2xl bg-bg-elevated/40">
        {isLoading ? (
          <div className="px-6 py-12 text-center text-text-secondary">Lade …</div>
        ) : entries.length === 0 ? (
          <EmptyTable
            message="Keine Einträge im gewählten Zeitraum."
            hint="Filter anpassen oder Zeitraum erweitern."
          />
        ) : table.rows.length === 0 ? (
          <EmptyTable message="Keine Treffer." hint="Suche oder Filter anpassen." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-bg-overlay/40">
              <tr className="text-left">
                <th className="px-4 py-2.5">
                  <SortableHeader
                    label="Zeitpunkt"
                    sortKey="time"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-4 py-2.5">
                  <SortableHeader
                    label="Mitarbeiter"
                    sortKey="user"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-4 py-2.5">
                  <SortableHeader
                    label="Aktion"
                    sortKey="action"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-4 py-2.5">
                  <SortableHeader
                    label="Bereich"
                    sortKey="resource"
                    currentKey={table.sortKey}
                    currentDir={table.sortDir}
                    onSort={table.toggleSort}
                  />
                </th>
                <th className="px-4 py-2.5">Details</th>
                <th className="px-4 py-2.5">IP</th>
                {isAdmin && <th className="px-4 py-2.5 w-px" aria-label="Aktionen" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {table.rows.map((e) => (
                <ActivityRow
                  key={e.id}
                  entry={e}
                  showRevert={isAdmin}
                  reverting={revertMutation.isPending && revertMutation.variables === e.id}
                  onRevert={async () => {
                    const ok = await confirm({
                      title: 'Aktion rückgängig machen?',
                      description:
                        'Gelöschte Datensätze werden wiederhergestellt. Beachte: kaskadierte Folge-Löschungen (z.B. Projekte unter einem Kunden) müssen ggf. separat zurückgesetzt werden.',
                      variant: 'primary',
                      confirmLabel: 'Wiederherstellen',
                    })
                    if (ok) revertMutation.mutate(e.id)
                  }}
                />
              ))}
            </tbody>
          </table>
        )}
      </TableShell>

      <Pager
        page={table.page}
        totalPages={table.totalPages}
        onPage={table.setPage}
        filteredRows={table.filteredRows}
        pageSize={table.pageSize}
      />
    </div>
  )
}

function ActivityRow({
  entry,
  showRevert,
  reverting,
  onRevert,
}: {
  entry: AuditEntry
  showRevert: boolean
  reverting: boolean
  onRevert: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const hasDetails =
    (entry.before != null && Object.keys(entry.before as object).length > 0) ||
    (entry.after != null && Object.keys(entry.after as object).length > 0)
  const tone = ACTION_TONES[entry.action] ?? 'bg-bg-overlay text-text-secondary'
  const wasReverted = !!entry.revertedAt
  const revertable = isRevertable(entry) && showRevert

  // Pull the affected entities out for the inline summary
  const affected = extractAffectedEntities(entry)

  return (
    <>
      <tr
        className={`hover:bg-bg-overlay/30 transition-colors ${hasDetails ? 'cursor-pointer' : ''} ${
          wasReverted ? 'opacity-60' : ''
        }`}
        onClick={() => hasDetails && setExpanded((v) => !v)}
      >
        <td className="px-4 py-2.5 text-text-secondary tabular-nums whitespace-nowrap">
          {fmtTime(entry.createdAt)}
        </td>
        <td className="px-4 py-2.5">
          <div className="text-text-primary">{entry.userName ?? '—'}</div>
          {entry.userEmail && (
            <div className="text-[11px] text-text-tertiary truncate max-w-[18ch]">
              {entry.userEmail}
            </div>
          )}
        </td>
        <td className="px-4 py-2.5">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
          >
            {ACTION_LABELS[entry.action] ?? entry.action}
          </span>
          {wasReverted && (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-bg-overlay text-text-tertiary px-2 py-0.5 text-2xs">
              ↩ rückgängig
            </span>
          )}
        </td>
        <td className="px-4 py-2.5 text-text-secondary">
          {RESOURCE_LABELS[entry.resource] ?? entry.resource}
        </td>
        <td className="px-4 py-2.5 text-text-secondary max-w-[28ch]">
          <AffectedSummary affected={affected} />
          {hasDetails && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((v) => !v)
              }}
              className="ml-2 text-xs text-accent hover:underline"
            >
              {expanded ? 'weniger' : 'mehr'}
            </button>
          )}
        </td>
        <td className="px-4 py-2.5 text-xs text-text-tertiary font-mono">{entry.ip ?? '—'}</td>
        {showRevert && (
          <td className="px-4 py-2.5 text-right">
            {revertable ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={reverting}
                onClick={(e) => {
                  e.stopPropagation()
                  onRevert()
                }}
                title="Rückgängig machen"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {reverting ? '…' : 'Rückgängig'}
              </Button>
            ) : null}
          </td>
        )}
      </tr>
      {expanded && hasDetails && (
        <tr className="bg-bg-overlay/20">
          <td colSpan={showRevert ? 7 : 6} className="px-4 py-3">
            <div className="space-y-3 text-xs">
              {affected.length > 0 && (
                <div>
                  <p className="uppercase tracking-wider text-text-tertiary mb-2">
                    Betroffene Datensätze ({affected.length})
                  </p>
                  <ul className="rounded border border-border-subtle bg-bg-base/40 divide-y divide-border-subtle">
                    {affected.map((a, i) => (
                      <li key={a.id ?? i} className="px-3 py-1.5 flex items-baseline gap-3">
                        <span className="text-text-primary font-medium">{a.label}</span>
                        {a.subtitle && (
                          <span className="text-text-tertiary text-[11px]">{a.subtitle}</span>
                        )}
                        {a.id && (
                          <span className="ml-auto font-mono text-2xs text-text-tertiary">
                            {a.id.slice(0, 8)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <details>
                <summary className="cursor-pointer text-text-tertiary hover:text-accent select-none">
                  Roh-Daten anzeigen (JSON)
                </summary>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {entry.before != null && Object.keys(entry.before as object).length > 0 && (
                    <div>
                      <p className="uppercase tracking-wider text-text-tertiary mb-1">Vorher</p>
                      <pre className="rounded bg-bg-base/60 border border-border-subtle p-2 overflow-auto max-h-48 text-text-secondary">
                        {JSON.stringify(entry.before, null, 2)}
                      </pre>
                    </div>
                  )}
                  {entry.after != null && Object.keys(entry.after as object).length > 0 && (
                    <div>
                      <p className="uppercase tracking-wider text-text-tertiary mb-1">Nachher</p>
                      <pre className="rounded bg-bg-base/60 border border-border-subtle p-2 overflow-auto max-h-48 text-text-secondary">
                        {JSON.stringify(entry.after, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
              {entry.resourceId && (
                <div className="text-text-tertiary">
                  Resource-ID: <code className="font-mono">{entry.resourceId}</code>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

interface AffectedEntity {
  id: string | null
  label: string
  subtitle: string | null
}

/**
 * Pull "what was actually touched" out of an audit row's before/after
 * payloads. Handles single-row deletes (`before.row`), bulk deletes
 * (`before.rows`), and create/update operations (`after`).
 */
function extractAffectedEntities(entry: AuditEntry): AffectedEntity[] {
  const before = entry.before as Record<string, unknown> | null
  const after = entry.after as Record<string, unknown> | null

  // bulk_delete: before.rows array
  const bulkRows = (before?.rows as Record<string, unknown>[] | undefined) ?? null
  if (Array.isArray(bulkRows) && bulkRows.length > 0) {
    return bulkRows.map((r) => entityFromRow(entry.resource, r))
  }
  // single delete: before.row
  const singleRow = (before?.row as Record<string, unknown> | undefined) ?? null
  if (singleRow && typeof singleRow === 'object') {
    return [entityFromRow(entry.resource, singleRow)]
  }
  // Otherwise: try after-payload for create/update
  if (after && typeof after === 'object') {
    const summary = humanLabelFromObject(after)
    if (summary) {
      return [{ id: entry.resourceId, label: summary, subtitle: null }]
    }
  }
  if (before && typeof before === 'object') {
    const summary = humanLabelFromObject(before)
    if (summary) {
      return [{ id: entry.resourceId, label: summary, subtitle: null }]
    }
  }
  return []
}

function entityFromRow(resource: string, row: Record<string, unknown>): AffectedEntity {
  const id = (row.id as string | undefined) ?? null
  switch (resource) {
    case 'customer':
      return {
        id,
        label: (row.name as string) ?? '—',
        subtitle: (row.billingCity as string) ?? null,
      }
    case 'project':
      return { id, label: (row.name as string) ?? '—', subtitle: null }
    case 'service':
      return { id, label: (row.name as string) ?? '—', subtitle: null }
    case 'tag':
      return { id, label: (row.name as string) ?? '—', subtitle: (row.color as string) ?? null }
    case 'time_entry': {
      const start = row.startTime
        ? new Date(row.startTime as string).toLocaleDateString('de-DE')
        : null
      return { id, label: (row.description as string) ?? 'Zeiteintrag', subtitle: start }
    }
    case 'absence':
      return {
        id,
        label: (row.type as string) ?? 'Abwesenheit',
        subtitle: row.fromDate
          ? new Date(row.fromDate as string).toLocaleDateString('de-DE')
          : null,
      }
    default:
      return { id, label: humanLabelFromObject(row) ?? '—', subtitle: null }
  }
}

function humanLabelFromObject(obj: Record<string, unknown>): string | null {
  const candidates = ['name', 'description', 'invoiceNumber', 'filename', 'type', 'email'] as const
  for (const key of candidates) {
    const v = obj[key]
    if (typeof v === 'string' && v.length > 0) return v
  }
  return null
}

function AffectedSummary({ affected }: { affected: AffectedEntity[] }) {
  if (affected.length === 0) return <span className="text-text-tertiary text-sm">—</span>
  if (affected.length === 1) {
    return (
      <span className="text-sm text-text-primary truncate inline-block max-w-full">
        {affected[0].label}
      </span>
    )
  }
  // Show first 2 names + "+ N weitere"
  const shown = affected
    .slice(0, 2)
    .map((a) => a.label)
    .join(', ')
  const more = affected.length - 2
  return (
    <span className="text-sm text-text-primary">
      {shown}
      {more > 0 && <span className="text-text-tertiary"> + {more} weitere</span>}
    </span>
  )
}
